import { IRecommendations } from 'entities/compilationContext/recommendations';
import { ITemplateLanguageStatus } from 'entities/template/template';
import { ILinkLookup, IPrecompiledLink, ITransformedLink } from './link-config';
import { OnsiteCompilationContext } from 'entities/compilationContext/OnsiteCompilationContext/OnsiteCompilationContext';
import { ICompilationContext } from 'entities/compilationContext/compilation-context';

export interface IOffsiteTemplatePreviewBody {
  type: string;
  value: string;
}

export interface ITemplatePreviewPayloadIds {
  compilationId: string;
  siteId: string;
  snapshotId: string;
  machineGuid: string;
}

export interface IOffsiteTemplatePreviewPayload {
  channel: string;
  featureType: string;
  bulkFeatureType?: string;
  template: IOffsiteTemplatePreviewBody;
  compilationContext: ICompilationContext;
  linkConfig: ILinkLookup[];
  ids: ITemplatePreviewPayloadIds;
  language: string;
  lookbackPeriod: number;
}

export interface OnsiteTemplatePreviewPayload {
  siteId: string;
  template: string;
  compilationContext: OnsiteCompilationContext;
}

export interface IConvertTemplateRequestPayload {
  siteId: string;
  template: string;
}

export interface IConvertTemplateResponse {
  siteId: string;
  originalTemplate: string;
  convertedTemplate: string;
}

export interface IOffsiteTemplatePreviewResponse {
  precompiled: string;
  compiled: string;
  precompiledLinks: IPrecompiledLink[];
  transformedLinks: ITransformedLink[];
  enrichments: IEnrichmentBrokerData;
}

/**
 * The response of the onsite template preview **__query__**. The query must output
 * `compiled` so that the response can be used within the template editor.
 */
export interface OnsiteTemplatePreviewQueryResponse {
  compiled: string;
  compilationContext: OnsiteCompilationContext;
}

/**
 * The actual response from the onsite template preview endpoint.
 */
export interface OnsiteTemplatePreviewResponse {
  template: string;
  compilationContext: OnsiteCompilationContext;
}

export interface IEnrichmentBrokerData {
  purchasedRecommendations?: IRecommendations[];
}

export interface IUpdateTemplatePayload {
  templateName: string;
  comment: string;
  template: string;
  enrichmentConfigIds: string[];
  status: string;
  /**
   * @deprecated - No longer required and will be removed in the future.
   *
   * See: https://salecycle.atlassian.net/browse/FLOW-2320
   */
  stylesheet?: string;
}

export interface IAddTemplateLanguagePayload {
  comment: string;
  template: string;
  type: string;
}

export interface ITemplateModifyLinkPayloadLinks {
  [linkId: string]: {
    skip?: string[];
    href?: string;
  };
}

export interface ITemplateModifyLinkPayload {
  template: IOffsiteTemplatePreviewBody;
  links: ITemplateModifyLinkPayloadLinks;
}

export interface ITemplateModifyLinkPayloadResponse {
  precompiled: string;
  links: IPrecompiledLink[];
}

export interface ITemplateValidationPayload {
  template: IOffsiteTemplatePreviewBody;
}

export enum TemplateSeverity {
  PASS = 'pass',
  WARNING = 'warning',
  FAIL = 'fail',
}

export interface ITemplateValidationCounts {
  [TemplateSeverity.PASS]: number;
  [TemplateSeverity.WARNING]: number;
  [TemplateSeverity.FAIL]: number;
}

export interface ITemplateValidationResult {
  counts: ITemplateValidationCounts;
  results?: {
    [validationName: string]: {
      severity: TemplateSeverity;
      count: number;
    };
  };
}

export interface ITemplateValidationRule {
  count: number;
  rule: string;
  severity: TemplateSeverity;
}

export interface SaveTemplatePayload {
  templateName: string;
  channel: string;
  type: string;
  template: string;
  enrichmentConfigIds?: string[];
  languages: string[];
  isEmbedded: boolean;
  /**
   * @deprecated - No longer required and will be removed in the future.
   *
   * See: https://salecycle.atlassian.net/browse/FLOW-2320
   */
  stylesheet?: string;
}

export interface TemplateContainer {
  enrichmentConfigIds: string[];
  templateName: string;
  type: string;
  channel: string;
  templateLanguages: ITemplateLanguageStatus[];
}

export type OnsiteTemplateContainer = Omit<TemplateContainer, 'templateLanguages'>;

export interface IRestoreTemplateVersionResponse {
  id: string;
  siteId: string;
  templateName: string;
  channel: string;
  type: string;
  template: string;
  language: string;
  versionId: string;
}

export interface IRestoreTemplateVersionRequestPayload {
  versionId: string;
}
